<template>
  <v-container class="ml-0" style="max-width: 820px">
    <AlertsAnimated :alerts="localAlerts" class="mb-4" />

    <v-skeleton-loader
      v-if="loading && !accountUser"
      type="article, list-item-three-line"
    />

    <template v-else-if="accountUser">
      <!-- Profile identity -->
      <v-card outlined class="mb-4">
        <v-card-text class="d-flex align-center">
          <v-avatar color="primary" size="56" class="mr-4 flex-shrink-0">
            <span class="white--text text-h6">{{ initials }}</span>
          </v-avatar>
          <div class="flex-grow-1" style="min-width: 0">
            <div class="text-h6 text-truncate">{{ fullName || "—" }}</div>
            <div
              class="caption grey--text text-truncate"
              style="font-family: monospace"
            >
              {{ accountUser.username }}
            </div>
            <div class="mt-2">
              <template v-if="accountUser.roles && accountUser.roles.length">
                <v-chip
                  v-for="(r, i) in accountUser.roles"
                  :key="i"
                  small
                  outlined
                  :color="roleScope(r.organization).color"
                  class="mr-1 mb-1"
                >
                  <v-icon left x-small>
                    {{ roleScope(r.organization).icon }}
                  </v-icon>
                  {{ roleLabel(r.role, r.organization) }}
                  <span v-if="r.organization" class="ml-1 grey--text">
                    · {{ r.organization.name }}
                  </span>
                </v-chip>
              </template>
              <span v-else class="grey--text caption">No role assigned</span>
            </div>
          </div>
          <v-btn
            small
            text
            color="primary"
            class="text-none align-self-start flex-shrink-0"
            @click="openEdit"
          >
            <v-icon left small>mdi-pencil</v-icon> Edit
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Contacts -->
      <v-card outlined class="mb-4">
        <v-card-title class="d-flex align-center text-subtitle-1">
          <v-icon color="primary" class="mr-2">
            mdi-card-account-mail-outline
          </v-icon>
          Contacts
          <v-chip x-small color="primary" outlined class="ml-2">
            {{ (accountUser.contactMethods || []).length }}
          </v-chip>
          <v-spacer />
          <v-btn small text color="primary" class="text-none" @click="openEdit">
            Manage
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div
            v-if="
              !accountUser.contactMethods || !accountUser.contactMethods.length
            "
            class="grey--text caption"
          >
            No contacts yet.
          </div>
          <div v-else class="contact-grid">
            <v-sheet
              v-for="cm in accountUser.contactMethods"
              :key="cm.id"
              outlined
              rounded="lg"
              class="d-flex align-center pa-3"
              :class="{ 'contact-tile--primary': cm.isDefault }"
              style="gap: 12px; min-width: 0"
            >
              <v-avatar :color="contactColor(cm.type)" size="38">
                <v-icon small dark>{{ contactIcon(cm.type) }}</v-icon>
              </v-avatar>
              <div style="min-width: 0; flex: 1">
                <div class="d-flex align-center" style="gap: 6px; min-width: 0">
                  <span
                    class="body-2 font-weight-medium text-truncate"
                    style="min-width: 0"
                  >
                    {{ cm.value }}
                  </span>
                  <v-chip
                    v-if="cm.isDefault"
                    x-small
                    color="primary"
                    class="flex-shrink-0"
                  >
                    Primary
                  </v-chip>
                </div>
                <div class="caption grey--text">
                  {{ contactTypeLabel(cm.type) }}
                </div>
              </div>
            </v-sheet>
          </div>
        </v-card-text>
      </v-card>

      <!-- Security -->
      <v-card outlined>
        <v-card-title class="text-subtitle-1">
          <v-icon color="primary" class="mr-2">mdi-shield-lock</v-icon>
          Security
        </v-card-title>
        <v-divider />
        <v-card-text>
          <template v-if="twoFactorEnabled">
            <div class="d-flex align-center flex-wrap py-2">
              <div class="mr-4">
                <div class="subtitle-2">Two-Factor Authentication</div>
                <div class="caption grey--text">
                  <template v-if="twoFactorMethodDetails">
                    {{ contactTypeLabel(twoFactorMethodDetails.type) }} ·
                    {{ twoFactorMethodDetails.value }}
                  </template>
                  <template v-else>Not set up</template>
                </div>
              </div>
              <v-spacer />
              <v-btn
                small
                outlined
                color="primary"
                class="text-none"
                @click="openTwoFa"
              >
                {{ accountUser.twoFactorMethod ? "Change" : "Set up" }}
              </v-btn>
            </div>

            <v-divider class="my-2" />
          </template>

          <div class="d-flex align-center flex-wrap py-2">
            <div class="mr-4">
              <div class="subtitle-2">Password</div>
              <div class="caption grey--text">
                Reset your password via a secure link sent to your primary
                contact.
              </div>
            </div>
            <v-spacer />
            <v-btn
              small
              outlined
              color="primary"
              class="text-none"
              :loading="passwordLoading"
              @click="changePassword"
            >
              Change password
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <!-- Edit Profile dialog -->
    <v-dialog v-model="editDialog" max-width="560" scrollable>
      <v-card>
        <v-card-title>
          Edit Profile
          <v-spacer />
          <v-btn icon @click="editDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="pt-4">
          <ValidationObserver ref="editObserver">
            <div class="text-overline primary--text font-weight-bold mb-2">
              Name
            </div>
            <v-row dense>
              <v-col cols="6">
                <ValidatedTextField
                  v-model="form.firstName"
                  name="First Name"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="6">
                <ValidatedTextField
                  v-model="form.lastName"
                  name="Last Name"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
            </v-row>

            <div
              class="text-overline primary--text font-weight-bold mt-5 mb-1 d-flex align-center"
            >
              Contacts
              <v-spacer />
              <v-btn
                x-small
                text
                color="primary"
                class="text-none"
                @click="addContact"
              >
                <v-icon left x-small>mdi-plus</v-icon> Add
              </v-btn>
            </div>
            <div class="caption grey--text mb-2">
              The starred contact is your primary — password resets and 2FA
              codes are sent there.
            </div>

            <div
              v-for="(cm, i) in form.contactMethods"
              :key="cm._key"
              class="d-flex align-center py-1"
              style="gap: 8px"
            >
              <template v-if="cm.id && !isEditableContact(cm.type)">
                <v-avatar :color="contactColor(cm.type)" size="30">
                  <v-icon x-small dark>{{ contactIcon(cm.type) }}</v-icon>
                </v-avatar>
                <span class="body-2 flex-grow-1 text-truncate">
                  {{ cm.value }}
                </span>
                <span class="caption grey--text">read-only</span>
              </template>
              <template v-else>
                <v-select
                  v-model="cm.type"
                  :items="addableContactTypes"
                  :disabled="!!cm.id"
                  outlined
                  dense
                  hide-details
                  style="max-width: 120px"
                  @change="cm.value = ''"
                />
                <InputPhoneNumber
                  v-if="cm.type !== 'email'"
                  v-model="cm.value"
                  :name="contactValueLabel(cm.type)"
                  required
                  show-failing
                  outlined
                  dense
                  hide-details="auto"
                  class="flex-grow-1"
                />
                <ValidatedTextField
                  v-else
                  v-model="cm.value"
                  :name="contactValueLabel('email')"
                  rules="required|email"
                  type="email"
                  outlined
                  dense
                  hide-details="auto"
                  class="flex-grow-1"
                />
                <v-btn
                  icon
                  small
                  :color="cm.isDefault ? 'primary' : 'grey'"
                  title="Set as primary"
                  @click="setPrimary(i)"
                >
                  <v-icon>
                    {{ cm.isDefault ? "mdi-star" : "mdi-star-outline" }}
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="grey darken-1"
                  title="Remove"
                  :disabled="form.contactMethods.length <= 1"
                  @click="removeContact(i)"
                >
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
            </div>

            <v-alert v-if="editError" type="error" text dense class="mt-3 mb-0">
              {{ editError }}
            </v-alert>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="saving"
            :disabled="!canSaveProfile"
            @click="saveProfile"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change 2FA method dialog -->
    <v-dialog
      v-model="twoFaDialog"
      max-width="460"
      scrollable
      @input="onTwoFaDialogToggle"
    >
      <v-card>
        <v-card-title>
          {{ accountUser && accountUser.twoFactorMethod ? "Change" : "Set up" }}
          Two-Factor
          <v-spacer />
          <v-btn icon @click="twoFaDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <!-- Step 1: choose the method the code is sent to -->
        <template v-if="twoFaStep === 'select'">
          <v-card-text class="px-4 pt-4 pb-0">
            <div class="caption grey--text mb-3">
              Choose where to receive your verification code. We'll send a
              one-time code there to confirm the change.
            </div>
            <v-radio-group
              v-model="selectedTwoFactorMethod"
              class="mt-0"
              hide-details
            >
              <v-sheet
                v-for="method in filteredContactMethods"
                :key="method.id"
                outlined
                rounded="lg"
                class="method-option d-flex align-center pa-3 mb-3"
                :class="{
                  'method-option--selected':
                    selectedTwoFactorMethod === method.id,
                }"
                @click="selectedTwoFactorMethod = method.id"
              >
                <v-avatar
                  size="40"
                  class="mr-3"
                  :color="
                    selectedTwoFactorMethod === method.id
                      ? 'primary'
                      : 'grey lighten-3'
                  "
                >
                  <v-icon
                    :color="
                      selectedTwoFactorMethod === method.id
                        ? 'white'
                        : 'grey darken-1'
                    "
                  >
                    {{ contactIcon(method.type) }}
                  </v-icon>
                </v-avatar>
                <div class="flex-grow-1" style="min-width: 0">
                  <div class="subtitle-2">
                    {{ contactTypeLabel(method.type) }}
                  </div>
                  <div class="caption grey--text text-truncate">
                    {{ method.value }}
                  </div>
                </div>
                <v-radio :value="method.id" class="ma-0 pr-1" />
              </v-sheet>
            </v-radio-group>
            <div
              v-if="!filteredContactMethods.length"
              class="caption grey--text"
            >
              Add an email or mobile contact first.
            </div>
            <v-alert
              v-if="twoFaError"
              type="error"
              text
              dense
              class="mt-2 mb-0"
            >
              {{ twoFaError }}
            </v-alert>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-4 py-3">
            <v-spacer />
            <v-btn text @click="twoFaDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              depressed
              :loading="sendingOtp"
              :disabled="
                !selectedTwoFactorMethod ||
                selectedTwoFactorMethod === accountUser.twoFactorMethod
              "
              @click="sendTwoFactorOtp"
            >
              Send code
            </v-btn>
          </v-card-actions>
        </template>

        <!-- Step 2: enter the OTP to confirm -->
        <template v-else>
          <v-card-text class="px-4 pt-4 pb-0">
            <div class="text-subtitle-2 grey--text text--darken-2 text-center">
              Enter the 6-digit code sent to
            </div>
            <div class="text-center font-weight-medium mb-4">
              {{ otpSentTo }}
            </div>
            <v-otp-input
              v-model="otpCode"
              length="6"
              type="number"
              :disabled="verifyingOtp"
              @finish="confirmTwoFactorChange"
            />
            <div class="text-center mt-3">
              <span v-if="resendCountdown > 0" class="caption grey--text">
                Resend code in {{ resendCountdown }}s
              </span>
              <v-btn
                v-else
                text
                small
                color="primary"
                class="text-none"
                :loading="sendingOtp"
                @click="resendTwoFactorOtp"
              >
                Resend code
              </v-btn>
            </div>
            <v-alert
              v-if="twoFaError"
              type="error"
              text
              dense
              class="mt-2 mb-0"
            >
              {{ twoFaError }}
            </v-alert>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-4 py-3">
            <v-btn text :disabled="verifyingOtp" @click="backToSelect">
              Back
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              depressed
              :loading="verifyingOtp"
              :disabled="!otpCode || otpCode.length < 6"
              @click="confirmTwoFactorChange"
            >
              Verify &amp; Save
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ACCOUNT_USER,
  UPDATE_USER,
  SEND_TWO_FACTOR_OTP,
  CONFIRM_TWO_FACTOR_CHANGE,
} from "./graphql";
import { roleLabel, roleScope } from "../users/roles";
import {
  ADDABLE_CONTACT_TYPES,
  contactIcon,
  contactColor,
  contactTypeLabel,
  contactValueLabel,
  isEditableContact,
} from "../users/contactMethods";
import useUser, { setCurrentUser } from "../authentication/useUser";
import ValidatedTextField from "@components/utils/ValidatedTextField.vue";
import InputPhoneNumber from "@tod-ui/components/InputPhoneNumber.vue";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import { ValidationObserver, extend } from "vee-validate";
import { email, required } from "vee-validate/dist/rules";

extend("required", { ...required, message: "{_field_} is required" });
extend("email", { ...email, message: "Enter a valid email address" });

export default {
  name: "MyAccountProfile",
  components: {
    ValidatedTextField,
    InputPhoneNumber,
    ValidationObserver,
    AlertsAnimated,
  },

  setup() {
    const { currentUser, requestPasswordReset } = useUser();
    const { localAlerts, addLocalAlert, clearAllLocalAlerts } = useAlerts();
    return {
      currentUser,
      requestPasswordReset,
      localAlerts,
      addLocalAlert,
      clearAllLocalAlerts,
      roleLabel,
      roleScope,
      contactIcon,
      contactColor,
      contactTypeLabel,
      contactValueLabel,
      isEditableContact,
      addableContactTypes: ADDABLE_CONTACT_TYPES,
    };
  },

  data() {
    return {
      accountUser: null,
      editDialog: false,
      twoFaDialog: false,
      form: { firstName: "", lastName: "", contactMethods: [] },
      cmKey: 0,
      saving: false,
      passwordLoading: false,
      selectedTwoFactorMethod: null,
      // 2FA change flow: "select" (choose method) → "otp" (enter code).
      twoFaStep: "select",
      twoFaToken: null,
      otpCode: "",
      otpSentTo: "",
      sendingOtp: false,
      verifyingOtp: false,
      twoFaError: null,
      resendCountdown: 0,
      resendTimer: null,
      editError: null,
    };
  },

  computed: {
    userId() {
      return this.currentUser?.id || null;
    },
    loading() {
      return this.$apollo.queries.accountUser.loading;
    },
    fullName() {
      if (!this.accountUser) return "";
      return `${this.accountUser.firstName || ""} ${
        this.accountUser.lastName || ""
      }`.trim();
    },
    initials() {
      const f = this.accountUser?.firstName?.[0] || "";
      const l = this.accountUser?.lastName?.[0] || "";
      const from = (f + l).toUpperCase();
      return from || this.accountUser?.username?.[0]?.toUpperCase() || "?";
    },
    twoFactorMethodDetails() {
      if (!this.accountUser?.twoFactorMethod) return null;
      return (this.accountUser.contactMethods || []).find(
        (m) => m.id === this.accountUser.twoFactorMethod
      );
    },
    // Show the 2FA setup control only when the user's role requires 2FA. A role
    // with `bypassTwoFactor` is exempt, so 2FA applies unless every role the
    // user holds bypasses it.
    twoFactorEnabled() {
      const roles = this.accountUser?.roles || [];
      if (!roles.length) return true;
      return roles.some((r) => !r.role?.bypassTwoFactor);
    },
    // 2FA can only be delivered to an email or mobile.
    filteredContactMethods() {
      return (this.accountUser?.contactMethods || []).filter((m) =>
        ["email", "mobile"].includes(m.type)
      );
    },
    canSaveProfile() {
      return (
        !!this.form.firstName &&
        !!this.form.lastName &&
        this.form.contactMethods.length > 0 &&
        this.form.contactMethods.every((cm) => !!cm.value)
      );
    },
  },

  apollo: {
    accountUser: {
      query: ACCOUNT_USER,
      variables() {
        return { id: this.userId };
      },
      skip() {
        return !this.userId;
      },
      update(data) {
        return data.user;
      },
      error() {
        this.addLocalAlert({
          type: "error",
          message: "Failed to load your account. Please try again.",
          timeout: 8,
        });
      },
    },
  },

  beforeDestroy() {
    this.clearResendTimer();
  },

  methods: {
    openEdit() {
      this.editError = null;
      this.form = {
        firstName: this.accountUser.firstName || "",
        lastName: this.accountUser.lastName || "",
        contactMethods: (this.accountUser.contactMethods || []).map((cm) => ({
          _key: this.cmKey++,
          id: cm.id,
          type: cm.type,
          value: cm.value,
          isDefault: !!cm.isDefault,
          isActive: cm.isActive !== false,
        })),
      };
      if (!this.form.contactMethods.length) this.addContact();
      this.editDialog = true;
    },
    addContact() {
      this.form.contactMethods.push({
        _key: this.cmKey++,
        type: "email",
        value: "",
        isDefault: this.form.contactMethods.length === 0,
        isActive: true,
      });
    },
    removeContact(index) {
      const removed = this.form.contactMethods.splice(index, 1)[0];
      // If we removed the primary, promote the first remaining contact.
      if (removed?.isDefault && this.form.contactMethods.length) {
        this.form.contactMethods[0].isDefault = true;
      }
    },
    setPrimary(index) {
      this.form.contactMethods.forEach((cm, i) => {
        cm.isDefault = i === index;
      });
    },
    // Maps the editable form rows to the `InputContactMethod` shape: existing
    // rows keep their id, new rows omit it.
    contactMethodsInput() {
      return this.form.contactMethods.map((cm) => ({
        ...(cm.id ? { id: cm.id } : {}),
        type: cm.type,
        value: cm.value,
        isDefault: cm.isDefault,
        isActive: cm.isActive !== false,
      }));
    },
    async saveProfile() {
      const valid = await this.$refs.editObserver.validate();
      if (!valid || !this.canSaveProfile) return;

      // Guarantee exactly one primary.
      if (!this.form.contactMethods.some((cm) => cm.isDefault)) {
        this.form.contactMethods[0].isDefault = true;
      }

      this.saving = true;
      this.editError = null;
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_USER,
          variables: {
            id: this.accountUser.id,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            contactMethods: this.contactMethodsInput(),
          },
        });

        // Keep the cached login user (header, facility) in sync with the edits.
        if (this.currentUser) {
          setCurrentUser({
            ...this.currentUser,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
          });
        }

        await this.$apollo.queries.accountUser.refetch();
        this.editDialog = false;
        this.flashSuccess("Profile updated.");
      } catch (err) {
        this.editError =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to update profile";
      } finally {
        this.saving = false;
      }
    },

    openTwoFa() {
      this.selectedTwoFactorMethod = this.accountUser.twoFactorMethod || null;
      this.resetTwoFaFlow();
      this.twoFaDialog = true;
    },
    // Reset the transient OTP state whenever the dialog is closed so a reopen
    // starts clean at the method-selection step.
    onTwoFaDialogToggle(open) {
      if (!open) this.resetTwoFaFlow();
    },
    resetTwoFaFlow() {
      this.twoFaStep = "select";
      this.twoFaToken = null;
      this.otpCode = "";
      this.otpSentTo = "";
      this.twoFaError = null;
      this.clearResendTimer();
    },
    backToSelect() {
      this.twoFaStep = "select";
      this.otpCode = "";
      this.twoFaError = null;
      this.clearResendTimer();
    },
    // Step 1 → send the OTP to the selected contact method, then advance to the
    // code-entry step.
    async sendTwoFactorOtp() {
      if (
        !this.selectedTwoFactorMethod ||
        this.selectedTwoFactorMethod === this.accountUser.twoFactorMethod
      ) {
        return;
      }
      this.sendingOtp = true;
      this.twoFaError = null;
      try {
        const response = await this.$apollo.mutate({
          mutation: SEND_TWO_FACTOR_OTP,
          variables: { contactMethodId: this.selectedTwoFactorMethod },
        });
        const result = response.data?.sendTwoFactorOtp;
        if (!result?.token) throw new Error("Failed to send verification code");
        this.twoFaToken = result.token;
        this.otpSentTo =
          result.contactMethod?.contact ||
          this.filteredContactMethods.find(
            (m) => m.id === this.selectedTwoFactorMethod
          )?.value ||
          "";
        this.otpCode = "";
        this.twoFaStep = "otp";
        this.startResendCountdown();
      } catch (err) {
        this.twoFaError =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Failed to send verification code";
      } finally {
        this.sendingOtp = false;
      }
    },
    async resendTwoFactorOtp() {
      // Resending mints a fresh session; sendTwoFactorOtp overwrites the token.
      await this.sendTwoFactorOtp();
    },
    // Step 2 → verify the OTP; on success the new method is applied server-side
    // and the user stays signed in.
    async confirmTwoFactorChange() {
      if (!this.otpCode || this.otpCode.length < 6 || !this.twoFaToken) return;
      this.verifyingOtp = true;
      this.twoFaError = null;
      try {
        const response = await this.$apollo.mutate({
          mutation: CONFIRM_TWO_FACTOR_CHANGE,
          variables: { token: this.twoFaToken, otp: this.otpCode },
        });
        const result = response.data?.confirmTwoFactorChange;
        if (!result) throw new Error("Verification failed");

        if (this.currentUser) {
          setCurrentUser({
            ...this.currentUser,
            twoFactorMethod: result.twoFactorMethod,
            skipTwoFactorUntil: result.skipTwoFactorUntil,
          });
        }
        await this.$apollo.queries.accountUser.refetch();
        this.twoFaDialog = false;
        this.flashSuccess("Two-factor authentication updated.");
      } catch (err) {
        this.otpCode = "";
        this.twoFaError =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Invalid or expired code. Please try again.";
      } finally {
        this.verifyingOtp = false;
      }
    },
    startResendCountdown() {
      this.clearResendTimer();
      this.resendCountdown = 30;
      this.resendTimer = setInterval(() => {
        if (this.resendCountdown > 0) {
          this.resendCountdown--;
        } else {
          this.clearResendTimer();
        }
      }, 1000);
    },
    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendCountdown = 0;
    },

    async changePassword() {
      const username = this.accountUser?.username;
      if (!username) return;
      this.passwordLoading = true;
      this.clearAllLocalAlerts();
      try {
        const [ok, message] = await this.requestPasswordReset(
          this.$apollo,
          username
        );
        if (ok) {
          this.addLocalAlert({
            type: "info",
            message:
              message ||
              "A password reset link has been sent to your registered contact.",
            timeout: 10,
          });
        } else {
          this.addLocalAlert({
            type: "error",
            message: message || "Failed to send the reset link.",
            timeout: 8,
          });
        }
      } finally {
        this.passwordLoading = false;
      }
    },

    flashSuccess(message) {
      this.addLocalAlert({ type: "success", message, timeout: 5 });
    },
  },
};
</script>

<style scoped>
/* CSS Grid auto-fit — no Vuetify equivalent: a lone contact stretches to fill the
   row while several wrap fluidly. (Border/radius/padding come from v-sheet.) */
.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
}
.contact-tile--primary {
  border-color: var(--v-primary-base) !important;
  background: rgba(var(--v-primary-base-rgb, 45, 46, 117), 0.05);
}

/* Selectable 2FA method cards (matches the 2FA setup screen). */
.method-option {
  cursor: pointer;
  border-color: rgba(0, 0, 0, 0.16) !important;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.method-option:hover {
  border-color: rgba(0, 0, 0, 0.32) !important;
  background-color: rgba(0, 0, 0, 0.02);
}
.method-option--selected {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(var(--v-primary-base-rgb, 45, 46, 117), 0.06);
}
</style>
